import React, { useState } from "react";
import BaseModal from "./BaseModal";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import { useLogTermsAccepted } from "../../hooks/useLogTermsAccepted";

interface TermsOfServiceModalProps {
  handleTermsAccepted: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
  handleTermsAccepted,
}) => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const logTermsAccepted = useLogTermsAccepted();

  const onAgreed = () => {
    // call hook to log terms accepted
    console.log("Called terms accepted");
    logTermsAccepted();
    // call parent function to hide terms
    handleTermsAccepted();
  };

  return (
    <>
      <BaseModal
        isOpen={true}
        title="Terms of Service"
        lastUpdated="December 2025"
        allowBackdropClose={false}
        showCloseButton={false}
        maxWidth="max-w-xl"
        maxHeight="max-h-[80vh]"
        footerButtonText="I Agree"
        onFooterButtonClick={onAgreed}
      >
        <div className="space-y-6 text-gray-700 text-sm">
          <p>
            Thank you for using Aveta Assistant. By accessing or using this
            application or any related services (“Services”), you agree to be
            bound by these Terms of Service (“Terms”). Please read them
            carefully, as they outline your rights and responsibilities.
          </p>

          <ol className="list-decimal list-inside space-y-2 [&::marker]:font-bold">
            <li>
              <strong>Eligibility.</strong> The Services are intended for
              individuals 18 years of age or older. By using the Services, you
              represent and warrant that you meet this requirement and have the
              authority to enter into these Terms.
            </li>
            <li>
              <strong>Purpose & Non-Clinical Nature.</strong> The Services
              provide general informational and support features only. They do
              not constitute medical advice, clinical guidance, diagnosis,
              treatment, or a substitute for professional judgment. The AI may
              generate or retrieve information, but such information is not
              tailored to patients or clinical circumstances and must not be
              relied upon for medical decision-making. Always seek the advice of
              a qualified healthcare professional for medical concerns.
            </li>
            <li>
              <strong>Prohibited Use.</strong> You agree not to use the Services
              for: Medical emergencies or urgent healthcare situations.;
              Inputting, uploading, or disclosing protected health information
              (PHI), medical records, or sensitive personal health data.; Any
              purpose related to storage, processing, or transmission of PHI;
              Illegal, harmful, deceptive, or abusive activities. The Services
              are not designed, intended, or permitted for clinical use or
              handling of regulated health information.
            </li>
            <li>
              <strong>User Responsibilities.</strong> You are responsible for:
              Maintaining the confidentiality of your account credentials; All
              activity that occurs under your account; Promptly notifying
              Meditrina of any unauthorized use or suspected security breach.
            </li>
            <li>
              <strong>Acceptable Use.</strong> You may use the Services only for
              lawful, personal, non-commercial purposes. You agree not to:
              Access or attempt to access the Services through unauthorized
              means; Reverse-engineer, decompile, modify, or interfere with any
              part of the Services; Transmit harmful, fraudulent, infringing, or
              misleading content; Scrape, harvest, or extract data from the
              Services without authorization.
            </li>
            <li>
              <strong>Intellectual Property.</strong> All content, software,
              design, trademarks, and other materials within the Services are
              owned by Meditrina. or its licensors. No rights are granted to you
              other than those expressly provided in these Terms. You may not
              reproduce, distribute, adapt, or create derivative works from the
              Services without prior written permission.
            </li>
            <li>
              <strong>Third-Party Links and Content.</strong> The Services may
              include links or content from third parties for convenience.
              Meditrina does not control and is not responsible for the
              availability, accuracy, or practices of third-party sites or
              content. You access such third-party resources at your own risk.
            </li>
            <li>
              <strong>
                Disclaimer of Warranties; Limitation of Liability.
              </strong>{" "}
              The Services are provided “AS IS” and “AS AVAILABLE,” without any
              warranties, express or implied, including warranties of accuracy,
              reliability, merchantability, fitness for a particular purpose, or
              non-infringement. To the maximum extent permitted by law,
              Meditrina shall not be liable for any direct, indirect,
              incidental, special, consequential, or punitive damages arising
              from or related to your use of (or inability to use) the Services.
              Meditrina is not responsible for decisions made or actions taken
              based on information from the Services, nor for any technical
              issues, interruptions, or data loss.
            </li>
            <li>
              <strong>Data Privacy.</strong> Meditrina handles personal data in
              accordance with its Privacy Policy and applicable law. The
              Services are not intended or appropriate for storage of medical
              records or regulated PHI. Please review the{" "}
              <button
                className="text-[#46ACC6] hover:underline"
                onClick={() => setShowPrivacyModal(true)}
                type="button"
              >
                Privacy Policy
              </button>{" "}
              for further information.
            </li>
            <li>
              <strong>Modifications to Services or Terms.</strong> Meditrina may
              update, modify, suspend, or discontinue any part of the Services
              at any time. We may also update these Terms from time to time.
              Continued use of the Services after changes become effective
              constitutes acceptance of the updated Terms.
            </li>
            <li>
              <strong>Feedback:</strong> If you provide feedback, comments, or
              suggestions, you grant Meditrina a non-exclusive, worldwide,
              royalty-free, perpetual license to use such feedback for any
              purpose without compensation or obligation to you
            </li>
          </ol>
        </div>
      </BaseModal>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </>
  );
};

export default TermsOfServiceModal;
